// Any numeric status the backend might return (400, 401, 403, 404, 422,
// 500, 502, ...). Narrowing this further forces "use as template" to lie
// about what was captured, so keep it open.
export type InjectedStatus = number;

// A per-op rule replaces the response with either:
//   - `customData` parsed as a `{ data?, errors? }` envelope, when set
//   - otherwise a synthetic GraphQL error built from `status` + `message`
// `status` and `message` are kept as separate fields so the panel can show
// quick-edit inputs that feed the "Simulate error" template button.
export interface Rule {
  status: InjectedStatus;
  message: string;
  // Full GraphQL result envelope as a JSON string: `{ data?, errors? }`.
  // Stored as text so mid-edit content survives a re-render without losing
  // the user's place; the link parses on every apply and silently falls
  // back to the synthetic error if the JSON is invalid or empty.
  customData?: string;
}

export type Outcome = "pending" | "ok" | "errored" | "simulated";

export type OperationType = "query" | "mutation" | "subscription" | "unknown";

export interface CapturedError {
  message: string;
  status?: number;
  path?: ReadonlyArray<string | number>;
  extensions?: Record<string, unknown>;
  source: "graphql" | "network";
  capturedAt: number;
}

export interface OpRecord {
  operationName: string;
  operationType: OperationType;
  lastFiredAt: number;
  lastDurationMs: number | null;
  lastOutcome: Outcome;
  callCount: number;
  lastRealError: CapturedError | null;
  // Last real (non-injected) `data` payload, deep-cloned at capture time so
  // future cache mutations can't reach in. Used as the template the
  // "Capture last" / "Empty arrays" buttons copy into the editor.
  lastData: unknown;
}

export type GlobalScope = "queries" | "mutations" | "both";

// Global rule always emits a synthetic error — it can't be a JSON envelope
// because the matching ops have varying response shapes.
export interface GlobalRule {
  scope: GlobalScope;
  status: InjectedStatus;
  message: string;
}

// What the link gets when it asks "does this op have an injection?".
//   - `envelope`: the user authored a full `{ data, errors }` shape — apply
//     as-is.
//   - `synthetic-error`: no envelope (per-op rule without JSON, or the
//     global rule); build a standard error response from status+message.
export type ResolvedInjection =
  | { kind: "envelope"; customData: string }
  | {
      kind: "synthetic-error";
      status: InjectedStatus;
      message: string;
    };

const LS_RULES_KEY = "td:devtools:errorInjection:rules";
const LS_GLOBAL_KEY = "td:devtools:errorInjection:global";
const LS_EXCLUSIONS_KEY = "td:devtools:errorInjection:exclusions";

// Per-op-type starting values used when the user first clicks `Override`.
// Queries typically fail with 500 (server-side); mutations with 400
// (validation/conflict). These are inert after creation — the user edits
// status/message inline on the rule afterwards. They feed `Add error`.
const INITIAL_RULE_BY_OP_TYPE = {
  query: { status: 500, message: "Simulated server error" },
  mutation: { status: 400, message: "Simulated client error" },
} as const;

// Textarea content the editor is pre-filled with on first Override. Gives
// the user a visible scaffold to either edit by hand or extend via the
// "Add data" / "Add error" buttons.
const INITIAL_CUSTOM_DATA = JSON.stringify({ data: {}, errors: [] }, null, 2);

function loadRules(): Map<string, Rule> {
  try {
    const raw = localStorage.getItem(LS_RULES_KEY);
    if (!raw) return new Map();
    const obj = JSON.parse(raw) as Record<
      string,
      Partial<Rule> & { mode?: unknown; appendError?: unknown }
    >;
    const out = new Map<string, Rule>();
    for (const [name, r] of Object.entries(obj)) {
      // Older persisted rules carried `mode` / `appendError` fields — silently
      // drop them. The rule is always "envelope or synthetic-error" now.
      const customData =
        typeof r.customData === "string" ? r.customData : undefined;
      out.set(name, {
        status: typeof r.status === "number" ? r.status : 500,
        message:
          typeof r.message === "string" ? r.message : "Simulated server error",
        ...(customData !== undefined ? { customData } : {}),
      });
    }
    return out;
  } catch {
    return new Map();
  }
}

function saveRules(rules: Map<string, Rule>) {
  try {
    localStorage.setItem(
      LS_RULES_KEY,
      JSON.stringify(Object.fromEntries(rules)),
    );
  } catch {
    /* quota / disabled storage — ignore */
  }
}

function loadGlobalRule(): GlobalRule | null {
  try {
    const raw = localStorage.getItem(LS_GLOBAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GlobalRule> & { mode?: unknown };
    return {
      scope: parsed.scope ?? "both",
      status: typeof parsed.status === "number" ? parsed.status : 500,
      message:
        typeof parsed.message === "string"
          ? parsed.message
          : "Simulated server error",
    };
  } catch {
    return null;
  }
}

function saveGlobalRule(rule: GlobalRule | null) {
  try {
    if (rule === null) localStorage.removeItem(LS_GLOBAL_KEY);
    else localStorage.setItem(LS_GLOBAL_KEY, JSON.stringify(rule));
  } catch {
    /* ignore */
  }
}

function loadExclusions(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_EXCLUSIONS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr)
      ? new Set(arr.filter((x): x is string => typeof x === "string"))
      : new Set();
  } catch {
    return new Set();
  }
}

function saveExclusions(set: Set<string>) {
  try {
    if (set.size === 0) localStorage.removeItem(LS_EXCLUSIONS_KEY);
    else localStorage.setItem(LS_EXCLUSIONS_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

class InjectionStore {
  private rules = loadRules();
  private globalRule = loadGlobalRule();
  private exclusions = loadExclusions();
  private recent = new Map<string, OpRecord>();
  private listeners = new Set<() => void>();
  private snapshotVersion = 0;
  private notifyScheduled = false;

  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  };

  // Version bumps on every change. Selectors return primitives based on this
  // so that `useSyncExternalStore` re-renders consumers without us cloning
  // the underlying Maps on every read.
  getVersion = () => this.snapshotVersion;

  private emit() {
    this.snapshotVersion++;
    if (this.notifyScheduled) return;
    this.notifyScheduled = true;
    queueMicrotask(() => {
      this.notifyScheduled = false;
      this.listeners.forEach((cb) => cb());
    });
  }

  getRules = (): ReadonlyMap<string, Rule> => this.rules;
  getGlobalRule = (): GlobalRule | null => this.globalRule;
  getExclusions = (): ReadonlySet<string> => this.exclusions;
  getRecent = (): ReadonlyMap<string, OpRecord> => this.recent;

  setExcluded(operationName: string, excluded: boolean) {
    const has = this.exclusions.has(operationName);
    if (excluded && !has) {
      this.exclusions.add(operationName);
      // Adding an exclusion supersedes any per-op rule for the same op —
      // the two states are mutually exclusive at the UI level too.
      if (this.rules.delete(operationName)) saveRules(this.rules);
      saveExclusions(this.exclusions);
      this.emit();
    } else if (!excluded && has) {
      this.exclusions.delete(operationName);
      saveExclusions(this.exclusions);
      this.emit();
    }
  }

  clearAllExclusions() {
    if (this.exclusions.size === 0) return;
    this.exclusions.clear();
    saveExclusions(this.exclusions);
    this.emit();
  }

  setGlobalRule(rule: GlobalRule | null) {
    this.globalRule = rule;
    saveGlobalRule(rule);
    this.emit();
  }

  updateGlobalRule(partial: Partial<GlobalRule>) {
    const base: GlobalRule = this.globalRule ?? {
      scope: "both",
      status: INITIAL_RULE_BY_OP_TYPE.query.status,
      message: INITIAL_RULE_BY_OP_TYPE.query.message,
    };
    this.setGlobalRule({ ...base, ...partial });
  }

  private initialValuesFor(operationName: string) {
    const opType = this.recent.get(operationName)?.operationType;
    return opType === "mutation"
      ? INITIAL_RULE_BY_OP_TYPE.mutation
      : INITIAL_RULE_BY_OP_TYPE.query;
  }

  setRule(operationName: string, partial: Partial<Rule>) {
    const existing = this.rules.get(operationName);
    const fallback = this.initialValuesFor(operationName);
    // Use `in` instead of `??` so an explicit empty string from the editor
    // (user cleared the JSON) wins over the existing value.
    let customData: string | undefined =
      "customData" in partial ? partial.customData : existing?.customData;
    // First-time creation seeds the editor with the empty envelope, so the
    // user starts from something concrete instead of a blank textarea.
    if (!existing && customData === undefined) {
      customData = INITIAL_CUSTOM_DATA;
    }
    const next: Rule = {
      status: partial.status ?? existing?.status ?? fallback.status,
      message: partial.message ?? existing?.message ?? fallback.message,
      ...(customData !== undefined ? { customData } : {}),
    };
    this.rules.set(operationName, next);
    saveRules(this.rules);
    // A per-op rule supersedes a user exclusion for the same op.
    if (this.exclusions.delete(operationName)) saveExclusions(this.exclusions);
    this.emit();
  }

  clearRule(operationName: string) {
    if (!this.rules.has(operationName)) return;
    this.rules.delete(operationName);
    saveRules(this.rules);
    this.emit();
  }

  clearAllRules() {
    if (this.rules.size === 0) return;
    this.rules.clear();
    saveRules(this.rules);
    this.emit();
  }

  // What the link should produce for this operation, or null to pass
  // through. Per-op rules win over the global rule, and a per-op rule with
  // an empty editor blocks global too — "I'm managing this op, currently
  // doing nothing" rather than silently letting the global apply.
  resolveInjection(
    operationName: string,
    operationType: OperationType,
  ): ResolvedInjection | null {
    const perOp = this.rules.get(operationName);
    if (perOp) {
      if (perOp.customData && perOp.customData.trim()) {
        return { kind: "envelope", customData: perOp.customData };
      }
      return null;
    }

    const global = this.globalRule;
    if (!global) return null;
    if (this.exclusions.has(operationName)) return null;
    const scopeMatches =
      global.scope === "both" ||
      (global.scope === "queries" && operationType === "query") ||
      (global.scope === "mutations" && operationType === "mutation");
    if (!scopeMatches) return null;
    return {
      kind: "synthetic-error",
      status: global.status,
      message: global.message,
    };
  }

  recordStart(operationName: string, operationType: OperationType) {
    const existing = this.recent.get(operationName);
    this.recent.set(operationName, {
      operationName,
      operationType,
      lastFiredAt: Date.now(),
      lastDurationMs: existing?.lastDurationMs ?? null,
      lastOutcome: "pending",
      callCount: (existing?.callCount ?? 0) + 1,
      lastRealError: existing?.lastRealError ?? null,
      lastData: existing?.lastData,
    });
    this.emit();
  }

  // `capturedData` uses a sentinel value to distinguish "not provided" (keep
  // existing) from "real value, including null" (overwrite). Using `undefined`
  // for the sentinel is fine because Apollo never gives us literal undefined
  // as a data payload — null yes, undefined no.
  recordEnd(
    operationName: string,
    outcome: Outcome,
    durationMs: number,
    capturedError: CapturedError | null = null,
    capturedData: unknown = undefined,
  ) {
    const existing = this.recent.get(operationName);
    if (!existing) return;
    this.recent.set(operationName, {
      ...existing,
      lastOutcome: outcome,
      lastDurationMs: durationMs,
      // Preserve previous sample on success so it stays available as a
      // template until the next real failure replaces it.
      lastRealError: capturedError ?? existing.lastRealError,
      lastData: capturedData !== undefined ? capturedData : existing.lastData,
    });
    this.emit();
  }

  // Seed a rule using a previously captured backend error as the
  // template — message and status copied into the rule's quick-edit
  // ammunition. The user then clicks "Simulate error" to materialize it.
  useErrorAsTemplate(operationName: string) {
    const op = this.recent.get(operationName);
    const sample = op?.lastRealError;
    if (!sample) return;
    const fallback = this.initialValuesFor(operationName);
    this.setRule(operationName, {
      status: sample.status ?? fallback.status,
      message: sample.message,
    });
  }

  // Parse the editor as a `{ data, errors }` envelope, mutate one field,
  // and write it back as JSON. If parsing fails or the editor is empty, we
  // start from the empty skeleton so the operation always succeeds.
  private mutateEnvelope(
    operationName: string,
    mutate: (env: { data: unknown; errors: unknown[] }) => void,
  ) {
    const existing = this.rules.get(operationName);
    let envelope: { data: unknown; errors: unknown[] };
    try {
      const raw = existing?.customData?.trim();
      const parsed = raw
        ? (JSON.parse(raw) as unknown)
        : { data: {}, errors: [] };
      const obj = (parsed && typeof parsed === "object" ? parsed : {}) as {
        data?: unknown;
        errors?: unknown;
      };
      envelope = {
        data: "data" in obj ? obj.data : {},
        errors: Array.isArray(obj.errors) ? [...obj.errors] : [],
      };
    } catch {
      envelope = { data: {}, errors: [] };
    }
    mutate(envelope);
    this.setRule(operationName, {
      customData: JSON.stringify(envelope, null, 2),
    });
  }

  // Replace the `data` field of the envelope with the last real response
  // captured for this operation. Leaves `errors` untouched so you can stack
  // it after an `addError` to simulate partial responses.
  addLastResponseAsData(operationName: string) {
    const op = this.recent.get(operationName);
    const snapshot = op?.lastData;
    if (snapshot === undefined || snapshot === null) return;
    this.mutateEnvelope(operationName, (env) => {
      env.data = snapshot;
    });
  }

  // Append a synthetic GraphQL error built from the rule's status +
  // message to the envelope's `errors` array. Additive: clicking twice
  // yields two errors. Composable with `addLastResponseAsData` for the
  // data-plus-error gotcha.
  addError(operationName: string) {
    const existing = this.rules.get(operationName);
    const fallback = this.initialValuesFor(operationName);
    const status = existing?.status ?? fallback.status;
    const message = existing?.message ?? fallback.message;
    this.mutateEnvelope(operationName, (env) => {
      env.errors.push({
        message,
        path: [operationName],
        extensions: { status, simulated: true },
      });
    });
  }

  clearTail() {
    if (this.recent.size === 0) return;
    this.recent.clear();
    this.emit();
  }
}

export const injectionStore = new InjectionStore();
